import { graphql } from "gatsby"
import get from "lodash/get"
import { Fragment } from "react"
import Badge from "../components/Badge"
import HomeCallToAction from "../components/HomeCallToAction"
import Billboard from "../components/BillboardV2"
import Image from "../components/ImageOptim"
import LinkCards from "../components/LinkCards"
import Link from "../components/LinkWrapper"
import MediaTile from "../components/MediaTile"
import SEO from "../components/SEO"
import { SlantTop } from "../components/Slants"
import BaseLayout from "../layouts/base"
import { capitalizeFirstLetter } from "../util"
import { getLocalizedString } from "../utils/getLocalizedString"
import { getUrlPrefix } from "../utils/getUrlPrefix"
import {
  articleTransformer,
  billboardV2Transformer,
  linkCardsTransformer,
  mediaTileTransformer,
  seoMetaTransformer,
} from "../utils/transformers"

const IndexPage = ({ data }) => {
  const {
    kontentItemHome: entry,
    allKontentItemArticle: articles,
    entryLocalizedStrings,
    global,
    localizedStrings,
    news,
  } = data
  if (!entry) {
    return null
  }
  const currentLanguage = entry.preferred_language
  const otherLanguages = entry.other_languages
  const billboards = entry.elements.billboards.value
  // The featured article is the featured_news_item if specified on the hompage,
  // if none is selected, it's the first article by post date
  const featuredArticle =
    get(entry, "elements.featured_news_item.value[0]") ||
    get(articles, "nodes[0]")
  const additionalArticles = articles.nodes.filter(
    (article) => article.id !== featuredArticle.id
  )
  const localizedViewAll =
    getLocalizedString(
      entryLocalizedStrings.nodes,
      "localization_string___view_all"
    ) || "View All"
  const localizedMoreNews =
    getLocalizedString(
      entryLocalizedStrings.nodes,
      "localization_string___more_news"
    ) || "More News"

  const servicesOverview = get(entry, "elements.services_overview.value[0]")
  const serviceCallouts = get(entry, "elements.services_callouts.value") || []
  const linkCards = linkCardsTransformer(entry)
  const callToAction = get(entry, "elements.call_to_action.value[0]")

  return (
    <BaseLayout
      currentLanguage={currentLanguage}
      entry={entry}
      otherLanguages={[entry, ...otherLanguages]}
      global={global}
      localizedStrings={localizedStrings}
      news={news}
    >
      {billboards && billboards.length > 0 ? (
        <Billboard {...billboardV2Transformer(billboards[0])} />
      ) : null}
      <div className="bg-white">
        <div className="container px-4 relative py-12 lg:py-16">
          <LinkCards
            linkCards={linkCards}
            columns={"3"}
            size="base"
            headingElement="h2"
          />
        </div>
      </div>
      <SlantTop />
      <div className="bg-gray-100 py-12 lg:py-16">
        <div className="container px-4 relative space-y-12 lg:space-y-16">
          <div>
            <MediaTile
              {...mediaTileTransformer(servicesOverview)}
              maxContainerWidth={1168}
            />
          </div>
          <div>
            <ServiceCallouts callouts={serviceCallouts} />
          </div>
          {callToAction ? (
            <div>
              <HomeCallToAction
                contentItem={callToAction}
                currentLanguage={currentLanguage}
              />
            </div>
          ) : null}
        </div>
      </div>
      <div className="bg-white border-t border-gray-300">
        <div className="container px-4 relative py-12 lg:py-16">
          <div className="flex -ml-4 md:-ml-12 -mt-8">
            <div className="pl-4 md:pl-12 w-full md:w-3/5 mt-8">
              <NewsPromo
                article={featuredArticle}
                localizedStrings={entryLocalizedStrings.nodes}
              />
            </div>
            <div className="pl-4 md:pl-12 mt-8 md:w-2/5 hidden md:block">
              <div className="flex border-b border-gray-300 justify-between items-baseline pb-1">
                {/* TODO: Create line-heights to match font size to match baseline across columns */}
                <h3
                  className="text-gray-700 font-bold leading-tight uppercase tracking-wide"
                  style={{ lineHeight: `24px` }}
                >
                  {localizedMoreNews}
                </h3>
                <Link
                  to={`${getUrlPrefix(currentLanguage)}/media/news`}
                  className="text-xs text-action-link hover:text-action transition-colors duration-150 hover:underline"
                >
                  {localizedViewAll}
                  <i className={`icon-chevron-right`}></i>
                </Link>
              </div>
              <ul className="text-sm divide-y divide-gray-300">
                {/* List the 4 most recent items by post date (the featured item is excluded)  */}
                {additionalArticles.slice(0, 4).map((article) => (
                  <li key={article.id} className="leading-tight">
                    <Link
                      to={article.resolved_url}
                      className="text-gray-700 hover:text-gray-600 font-bold block py-3 bg-transparent transition-colors duration-150"
                    >
                      <span
                        className={`block text-gray-600 font-normal text-xs leading-tight`}
                      >
                        {capitalizeFirstLetter(
                          article.elements.post_date.value
                        )}
                      </span>
                      {article.elements.title.value}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </BaseLayout>
  )
}

export const Head = ({ data }) => {
  const entry = data.kontentItemHome
  const global = data.global

  return <SEO {...seoMetaTransformer(entry, global)} />
}

const NewsPromo = ({ article, localizedStrings }) => {
  const transformedArticle = articleTransformer(article, {
    localizedStrings: localizedStrings,
  })
  const localizedReadMore =
    getLocalizedString(localizedStrings, "localization_string___read_more") ||
    "Read More"

  return (
    <div className="flex flex-wrap -ml-6 md:-ml-8 -mt-8">
      {transformedArticle.image ? (
        <div className="w-1/3 pl-6 md:pl-8 mt-8">
          <Link
            to={transformedArticle.url}
            className={`block border-gray-300 border-4 hover:border-gray-300 duration-150 transition-colors`}
          >
            <Image
              {...transformedArticle.image}
              width={168}
              cropRatio={0.525}
            />
          </Link>
        </div>
      ) : null}
      <div className="pl-6 md:pl-8 w-2/3 mt-8">
        <h4 className="font-bold text-lg leading-tight">
          <Link
            to={transformedArticle.url}
            className="text-gray-900 hover:text-gray-600 duration-150 transition-colors"
          >
            {transformedArticle.title}
          </Link>
        </h4>
        <div
          className={`flex -ml-4 text-xs mt-1 mb-2 text-gray-600 items-center`}
        >
          <div className={`pl-4`}>
            <Badge
              theme={
                {
                  article: "orange",
                  news: "orange",
                  post: "cyan",
                  spotlight: "gray",
                }[transformedArticle.typeCodename]
              }
            >
              {transformedArticle.type}
            </Badge>
          </div>
          <div className={`pl-4`}>
            {capitalizeFirstLetter(transformedArticle.postDate)}
          </div>
        </div>
        <div
          className="mt-4 text-sm text-gray-800  leading-normal"
          dangerouslySetInnerHTML={{ __html: transformedArticle.summary }}
        />
        <p className="mt-2">
          <Link
            to={transformedArticle.url}
            className="text-action-link hover:text-action-active text-sm duration-150 transition-colors hover:underline"
          >
            {localizedReadMore}
            <i className="icon-chevron-right" aria-hidden></i>
          </Link>
        </p>
      </div>
    </div>
  )
}

const ServiceCallouts = ({ callouts }) => {
  return (
    <div
      className={`flex flex-col sm:flex-row flex-wrap -mt-8 justify-center text-left`}
    >
      {callouts.map((item, index) => {
        const value = get(item, "elements.value.value")
        const appendValue = get(item, "elements.append_value.value")
        const label = get(item, "elements.label.value")

        return (
          <Fragment key={index}>
            {/* Adds a horizontal divider between rows on sm & md screens */}
            {index > 0 && index % 2 === 0 && (
              <hr
                className={`hidden sm:block lg:hidden w-full border-t border-gray-300 mt-8`}
              />
            )}
            <div
              className={`w-56 max-w-1/2 sm:w-1/2 sm:max-w-none ${
                callouts.length > 2 ? `lg:w-1/5` : `lg:w-1/2`
              } grow mt-8 border-gray-300 ${
                index > 0
                  ? `border-t sm:border-t-0 pt-8 sm:pt-0 lg:border-l lg:pl-8 xl:pl-12`
                  : ``
              } ${index % 2 === 1 ? `sm:border-l sm:pl-12` : ``}`}
              key={get(item, "system.id")}
            >
              <span
                className={`mb-2 text-cyan text-4xl font-bold leading-none uppercase block w-56 lg:w-40 xl:w-48 max-w-full`}
              >
                {value}
                {appendValue ? (
                  <span className="pl-1 inline uppercase tracking-wide text-base text-gray-900">
                    {appendValue}
                  </span>
                ) : null}
              </span>
              <span
                className={`text-sm font-bold leading-tight text-gray-700 uppercase block w-56 lg:w-40 xl:w-48 max-w-full`}
              >
                {label}
              </span>
            </div>
          </Fragment>
        )
      })}
    </div>
  )
}
export default IndexPage

export const query = graphql`
  fragment ArticleListing on kontent_item_article {
    id
    resolved_url
    system {
      type
      language
    }
    elements {
      article_type {
        value {
          codename
        }
      }
      title {
        value
      }
      url_slug {
        value
      }
      post_date {
        value(formatString: "MMMM D, Y", locale: $locale_for_date_formatter)
      }
      headline {
        value
      }
      summary {
        value
      }
      featured_media {
        value {
          ... on kontent_item_image_element {
            elements {
              image__asset {
                value {
                  url
                }
              }
              image__alt_text {
                value
              }
            }
          }
        }
      }
    }
  }

  query HomePageQuery($language: String!, $locale_for_date_formatter: String!) {
    ...Global
    kontentItemHome(preferred_language: { eq: $language }) {
      preferred_language
      resolved_url
      other_languages {
        resolved_url
        preferred_language
        system {
          type
          language
        }
      }
      system {
        type
        language
      }
      elements {
        seo_metadata__meta_title {
          value
        }
        seo_metadata__meta_description {
          value
        }
        seo_metadata__open_graph_description {
          value
        }
        seo_metadata__open_graph_title {
          value
        }
        seo_metadata__seo_image {
          value {
            ...Image
          }
        }
        billboards {
          value {
            ... on Node {
              ...BillboardV2
            }
          }
        }
        featured_news_item {
          value {
            ...ArticleListing
          }
        }
        services_overview {
          value {
            ...MediaTile
          }
        }
        services_callouts {
          value {
            ...CallOut
          }
        }
        link_cards {
          value {
            ...LinkCard
          }
        }
        call_to_action {
          value {
            ...CallToAction
          }
        }
      }
    }
    allKontentItemArticle(
      limit: 5
      sort: { elements: { post_date: { value: DESC } } }
      filter: {
        preferred_language: { eq: $language }
        system: { language: { eq: $language } }
        elements: {
          title: { value: { nin: ["", "Dummy Article"] } }
          hide_from_homepage: { value: { ne: "true" } }
        }
      }
    ) {
      nodes {
        ...ArticleListing
      }
    }
    entryLocalizedStrings: allKontentItemLocalizationString(
      filter: {
        preferred_language: { eq: $language }
        system: {
          codename: {
            in: [
              "localization_string___insights"
              "localization_string___more_news"
              "localization_string___news"
              "localization_string___read_more"
              "localization_string___spotlight"
              "localization_string___view_all"
            ]
          }
        }
      }
    ) {
      nodes {
        system {
          codename
        }
        elements {
          localized_string_value {
            value
          }
        }
      }
    }
  }
`
